/**
 * AdminCPDPoints.js — "My CPD Points" account dashboard page.
 *
 * Displays a breakdown of the logged-in user's CPD points by category,
 * lists individual CPD activities, and allows manual CPD record entry
 * via the ManuallyCPD modal. Data is fetched from the admin panel API.
 * Protected — redirects to home if not logged in.
 *
 * Props:
 *  - Good {string}                Time-of-day greeting.
 *  - EngineURL {string}           Base URL for the engine API.
 *  - AdminPanelURL {string}       Base URL for the admin panel API.
 *  - useLoggedIn {boolean}        Whether the user is authenticated.
 *  - loggedInUserData {object}    Logged-in user details.
 *  - UpdateCartQty {Function}     Updates the cart badge.
 *  - UpdateCartAmount {Function}  Updates the cart total.
 *  - ShowLoginPopup {Function}    Opens the login modal.
 *  - ShowLoginPopup_ {Function}   Redirects to profile if logged in, else shows login.
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React, { useState, useEffect, Fragment } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import { LoaderIcon } from "../../icons";
import PlusIconImg from "../../images/plus.png";
import DisclaimerSvg from "../../images/disclaimer.svg";
import { Container, Row, Col, ListGroup } from "react-bootstrap";
import pm from "../../images/pm-new.svg";
import ps from "../../images/ps-new.svg";
import sl from "../../images/sl-new.svg";
import ep from "../../images/ep-new.svg";
import axios from "axios";
import { Helmet } from "react-helmet";
import { AdminSidebar } from "./Sidebar/AdminSidebar";
import { ManuallyCPD } from "../Modals/ManuallyCPD";
import DownloadIcon from "../../images/Download.svg";

export const AdminCPDPoints = (props) => {
  let history = useHistory();
  const My_Account_button_click = () => {
    if (props.useLoggedIn) history.push("/admin-my-library");
    else props.ShowLoginPopup_();
  };

  // Orders states
  let cpd_years_index = 0;
  const [cpdPoints, setCpdPoints] = useState([]);
  const [CustomcpdPoints, setCustomcpdPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [program_years_data_past_upcoming, set_program_years_data_past_upcoming] = useState([]);
  const [userEmail, set_user_email] = useState("");
  const [userToken, set_user_token] = useState(undefined);
  const [userFirstname, set_user_firstname] = useState("");
  const [userLastname, set_user_lastname] = useState("");
  
  async function getData() {
    if (userEmail !== "" && userLastname !== "") {
      await axios
        .post(
          `${props.EngineURL}salesforce-query/checkout/leo_cpd.php`,
          {
            contact_id: userEmail,
            user_lname: userLastname,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${userToken}`,
            },
          }
        )
        .then(function (response) {
          if (response.data.error !== undefined && response.data.error !== "") {
          } else {
            setCpdPoints(response.data.program_years_data ?? {});
            setCustomcpdPoints(response.data.custom_points ?? {});
            set_program_years_data_past_upcoming(response.data.program_years_data_past_upcoming ?? {});
          }
          setLoading(false);
          return response;
        })
        .catch(function (error) {
          setLoading(false);
        })
        .finally(function () {
          setLoading(false);
        });
    }
  }

  useEffect(() => {
    if (props.loggedInUserData !== null && props.loggedInUserData !== undefined) {
      set_user_email(props.loggedInUserData.email);
      set_user_firstname(props.loggedInUserData.firstName);
      set_user_lastname(props.loggedInUserData.lastName);
      set_user_token(props.loggedInUserData.accessToken);
    }
  }, [props.loggedInUserData]);

  useEffect(() => {
    if (userToken !== "" && userToken !== undefined) {
      getData();
    }
  }, [userToken]);

  const doLogout = () => {
    axios
      .post(
        props.EngineURL + "create-account/2fa/delete_2fa.php",
        {
          email: props.UpdateloginUserDetail.email,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`, // Replace `yourToken` with your actual token variable
          },
        }
      )
      .then(function (response) {
        localStorage.removeItem("userDetail");
        localStorage.setItem("isLoggedin", false);
        localStorage.removeItem("checkout_token");
        localStorage.removeItem("thank_you_image");
        localStorage.removeItem("checkout_id");
        localStorage.removeItem("cart_session");
        localStorage.removeItem("create_account_detail");
        localStorage.removeItem("cart_session_sku");
        localStorage.removeItem("isLoggedin");
        localStorage.removeItem("was_book");
        localStorage.removeItem("wishlist_item");
        window.location.href = "/";
      })
      .catch(function (error) {
        console.log(error);
      });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
  };

  // With this — tracks which year is downloading + any error message
  const [pdfDownloadingYear, setPdfDownloadingYear] = useState(null);
  const [pdfError, setPdfError] = useState({});


  const CPD_PDF = (data, cpd_year_id) => {
    setPdfDownloadingYear(cpd_year_id); // only disable this year's button
    setPdfError("");

    const cpd_history = [];

    data.forEach((year_program_inner) => {
      cpd_history.push({
        date: year_program_inner.program_start_date_format,
        name: year_program_inner.program_name,
        ethics: year_program_inner.Ethics_Professional_Responsibility__c,
        practice: year_program_inner.Practice_Management_Business_Skills__c,
        professional: year_program_inner.Professional_Skills__c,
        substantive: year_program_inner.Substantive_Law__c,
      });
    });

    if (CustomcpdPoints[cpd_year_id] !== undefined) {
      CustomcpdPoints[cpd_year_id].forEach((item) => {
        cpd_history.push({
          date: formatDate(item.program_date),
          name: item.program_name,
          ethics: item.ep,
          practice: item.pm,
          professional: item.ps,
          substantive: item.sl,
        });
      });
    }

    const SendData = {
      year: cpd_year_id,
      name: userFirstname + " " + userLastname,
      email: userEmail,
      cpd_points: cpd_history,
    };

    axios
      .post(props.AdminPanelURL + "api/cpd_pdf", SendData, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${userToken}`,
          'X-API-KEY': props.API_KEY   // ← Add this
        },
      })
      .then(function (response) {
        if (response.status === 200 && response.data.pdf) {
          const newTab = window.open(response.data.pdf, "_blank");
          if (!newTab) {
            setPdfError(prev => ({ ...prev, [cpd_year_id]: "Popup blocked — " }));
            window.__cpd_pdf_url = response.data.pdf;
          } else {
            newTab.focus();
          }
        } else {
          setPdfError(prev => ({ ...prev, [cpd_year_id]: "PDF could not be generated. Please try again." }));
        }
      })
      .catch(function (error) {
        let msg;

        if (error.response?.status === 429) {
          const retryAfter = error.response.headers['retry-after'];
          msg = retryAfter
            ? `Too many requests. Please try again in ${retryAfter} seconds.`
            : "Too many requests. Please wait a moment and try again.";
        } else if (error.response?.status === 500) {
          msg = "Server error generating PDF. Please try again later.";
        } else if (error.response?.status === 419) {
          msg = "Your session has expired. Please refresh the page and try again.";
        } else if (error.request) {
          msg = "Network error. Please check your connection.";
        } else {
          msg = "Something went wrong. Please try again.";
        }

        setPdfError(prev => ({ ...prev, [cpd_year_id]: msg }));
      })
      .finally(function () {
        setPdfDownloadingYear(null);
      });
  };

  function download_csv_file(csvFileData) {
    //define the heading for each row of the data
    var csv = "Name,Type,Date,EP,PM,PS,SL,TOTAL\n";

    //merge the data with CSV
    csvFileData.forEach(function (row) {
      csv += row.join(",");
      csv += "\n";
    });

    //display the created CSV data on the web browser
    //document.write(csv);

    var hiddenElement = document.createElement("a");
    hiddenElement.href = "data:text/csv;charset=utf-8," + encodeURI(csv);
    // hiddenElement.target = '_blank';

    //provide the name for the CSV file to be downloaded
    hiddenElement.download = "CPD points.csv";
    hiddenElement.click();
  }

  const exportCpdPoints = (cpd_year_id) => {
    if (cpd_year_id !== "") {
      let cpdPointsExport = cpdPoints[cpd_year_id];
      cpdPointsExport = [...cpdPointsExport, ...CustomcpdPoints[cpd_year_id]]
      var csvFileData = [];
      let export_subtotal = 0;      

      cpdPointsExport.forEach((export_item) => {

        const ep = parseFloat(export_item.Ethics_Professional_Responsibility__c ?? export_item.ep ?? 0);
        const pm = parseFloat(export_item.Practice_Management_Business_Skills__c ?? export_item.pm ?? 0);
        const ps = parseFloat(export_item.Professional_Skills__c ?? export_item.ps ?? 0);
        const sl = parseFloat(export_item.Substantive_Law__c ?? export_item.sl ?? 0);
        const name = export_item.program_name;
        const type = export_item.product_record_type_name ?? export_item.program_format ?? '';
        const date = export_item.program_start_date_format ?? export_item.program_date ?? '';
        const row_total = ep + pm + ps + sl;
        export_subtotal = export_subtotal + row_total;

        let new_row = [
          name,
          type,
          date,
          ep,
          pm,
          ps,
          sl,
          row_total
        ];
        csvFileData.push(new_row);
      });

      csvFileData.push(["", "", "", "", "", "", "", export_subtotal]);

      download_csv_file(csvFileData);
    }
  };

  const [cdp_point_popup, set_cpd_point_popup] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const location = useLocation();
  const routeLabels = {
    'admin-profile': 'My Profile',
    'admin-my-events': 'Upcoming Events',
    'admin-my-library': 'My Digital Library',
    'admin-cpd-points': 'CPD Points',
    'my-wishlist': 'Bookmarks',
    'admin-orders': 'Billing History',
  };
  const activeLabel = routeLabels[location.pathname.replace('/', '')] || 'Menu';

  const randm = () => {
    return (Math.random() + 1).toString(36).substring(7);
  };
  const twoDigit = (n) => {
    return n > 9 ? "" + n : "0" + n;
  };
  const currentMonth = new Date().getMonth() + 1;
  const CPDyear = () => {
    return currentMonth <= 3
      ? parseInt(new Date().getFullYear() - 1) + "-" + new Date().getFullYear()
      : new Date().getFullYear() + "-" + parseInt(new Date().getFullYear() + 1);
  };
  return (
    <>
      <div className="admin_page admin_cpd_points">
        <Helmet>
          <title>My CPD Points | Leo Cussen Centre for Law</title>
        </Helmet>
        <Container>
          {props.useLoggedIn !== undefined && props.useLoggedIn === true ? (
            <div className="admin_wrapper position-relative">
              {props.loggedInUserData !== null && props.loggedInUserData !== undefined ? (
                <div className="admin_details_block">
                  <p className="ae-greeting-text">{props.Good},</p>
                  <h1 className="ae-greeting-name">{props.loggedInUserData.firstName}</h1>
                </div>
              ) : null}
              <Row>
                <Col xl={3} lg={4} xs={12} className="d-none d-lg-block">
                  <AdminSidebar doLogout={doLogout} />
                </Col>
                <Col xl={9} lg={8} xs={12}>
                  <div className="admin_right_content_block">
                    <div className="ae-disclaimer">
                      <div className="ae-disclaimer-icon"><img src={DisclaimerSvg} alt="" /></div>
                      <div className="ae-disclaimer-body">
                        <p className="ae-disclaimer-title">Disclaimer</p>
                        <p className="ae-disclaimer-text">CPD customers please note that we cannot certify your attendance at our programs, only that you have registered to attend*. CPD point history is retained for the current and previous CPD years (1 April – 31 March). For enquiries contact <a href="mailto:registration@leocussen.edu.au">registration@leocussen.edu.au</a>.</p>
                        <p className="ae-disclaimer-text" style={{ marginTop: '8px' }}>*Attendance at regulatory programs such as our Practice Management Course, Supervised Legal Traineeship Workshops and Mental Health First Aid for Legal Professionals Accredited Workshops can be certified.</p>
                      </div>
                    </div>
                    <div className="admin_head d-flex flex-wrap align-items-center justify-content-between">
                      <h4 className="m-0">My CPD Points</h4>
                      <div className="manually_add_point d-flex align-items-center" onClick={() => set_cpd_point_popup(true)}>
                        <img src={PlusIconImg} alt="" className="manually_add_icon" /> Manually add points
                      </div>
                    </div>
                    {cpdPoints !== null && cpdPoints !== undefined && Object.keys(cpdPoints).length > 0 ? (
                      <div className="cpd_point_data_wrapper">

                        {Object.keys(cpdPoints).map((cpd_event_year) => {

                          let cpd_year_id = cpd_event_year;
                          let cpd_year_programs = cpdPoints[cpd_year_id];
                          cpd_years_index = cpd_years_index + 1;
                          let year_type =
                            program_years_data_past_upcoming !== undefined && program_years_data_past_upcoming[cpd_year_id] !== undefined
                              ? program_years_data_past_upcoming[cpd_year_id]
                              : "";
                          let Practice_Management_Business_Skills_otal_points = cpd_year_programs.reduce(function (sum, current) {
                            var temp =
                              current.Practice_Management_Business_Skills__c !== null ? parseFloat(current.Practice_Management_Business_Skills__c) : 0;
                            return parseFloat(sum) + temp;
                          }, 0);

                          let Professional_Skills_total_points = cpd_year_programs.reduce(function (sum, current) {
                            var temp = current.Professional_Skills__c !== null ? parseFloat(current.Professional_Skills__c) : 0;
                            return parseFloat(sum) + temp;
                          }, 0);

                          let Substantive_Law_total_points = cpd_year_programs.reduce(function (sum, current) {
                            var temp = current.Substantive_Law__c !== null ? parseFloat(current.Substantive_Law__c) : 0;
                            return parseFloat(sum) + temp;
                          }, 0);

                          let Ethics_total_points = cpd_year_programs.reduce(function (sum, current) {
                            var temp =
                              current.Ethics_Professional_Responsibility__c !== null ? parseFloat(current.Ethics_Professional_Responsibility__c) : 0;
                            return parseFloat(sum) + temp;
                          }, 0);

                          if (CustomcpdPoints[cpd_year_id] !== undefined) {
                            Practice_Management_Business_Skills_otal_points += CustomcpdPoints[cpd_year_id].reduce(function (sum, current) {
                              return parseFloat(sum) + parseFloat(current.pm);
                            }, 0);

                            Professional_Skills_total_points += CustomcpdPoints[cpd_year_id].reduce(function (sum, current) {
                              return parseFloat(sum) + parseFloat(current.ps);
                            }, 0);

                            Substantive_Law_total_points += CustomcpdPoints[cpd_year_id].reduce(function (sum, current) {
                              return parseFloat(sum) + parseFloat(current.sl);
                            }, 0);

                            Ethics_total_points += CustomcpdPoints[cpd_year_id].reduce(function (sum, current) {
                              return parseFloat(sum) + parseFloat(current.ep);
                            }, 0);
                          }

                          let total_points =
                            parseFloat(Practice_Management_Business_Skills_otal_points) +
                            parseFloat(Professional_Skills_total_points) +
                            parseFloat(Substantive_Law_total_points) +
                            parseFloat(Ethics_total_points);


                          return (
                            <Fragment key={cpd_years_index}>
                              <div className="admin_head d-flex flex-wrap align-items-center justify-content-between">
                                <h5 className="text-secondary display-5 w-100 mb-2">
                                  {cpd_year_id}{" "}
                                  {/* After — only this year's button is disabled, others still work */}

                                  <button className={`downloadpdf ${pdfDownloadingYear === cpd_year_id ? 'disabled' : ''}`}
                                    onClick={() => pdfDownloadingYear === null && CPD_PDF(cpd_year_programs, cpd_year_id)}
                                    style={{
                                      pointerEvents: pdfDownloadingYear === cpd_year_id ? 'none' : 'auto',
                                      opacity: pdfDownloadingYear === cpd_year_id ? 0.6 : 1
                                    }}
                                  >
                                    {pdfDownloadingYear === cpd_year_id ? "Processing…" : "Download PDF"}
                                    <img src={DownloadIcon} alt="" className="downloadpdf-icon" />
                                  </button>

                                </h5>
                                {pdfError[cpd_year_id] && (
                                  <p className="text-danger w-100 m-0" style={{ fontSize: '13px' }}>
                                    {pdfError[cpd_year_id]}
                                    {pdfError[cpd_year_id].startsWith("Popup blocked") && window.__cpd_pdf_url && (
                                      <a href={window.__cpd_pdf_url} target="_blank" rel="noopener noreferrer"
                                        style={{ marginLeft: '4px' }}>
                                        click here to open PDF
                                      </a>
                                    )}
                                  </p>
                                )}
                                <h5 className="m-0 h5 text-dark">My CPD Points: {total_points}</h5>
                                <ListGroup horizontal as="ul" className="justify-content-between cpd_topics">
                                  <ListGroup.Item as="li">
                                    <span>{Ethics_total_points}</span>
                                    <img src={ep} alt="Ethics & Professional Responsibility" title="Ethics & Professional Responsibility" />
                                  </ListGroup.Item>
                                  <ListGroup.Item as="li">
                                    <span>{Practice_Management_Business_Skills_otal_points}</span>
                                    <img src={pm} alt="Practice Management & Business Skills" title="Practice Management & Business Skills" />
                                  </ListGroup.Item>
                                  <ListGroup.Item as="li">
                                    <span>{Professional_Skills_total_points}</span>
                                    <img src={ps} alt="Professional Skills" title="Professional Skills" />
                                  </ListGroup.Item>
                                  <ListGroup.Item as="li">
                                    <span>{Substantive_Law_total_points}</span>
                                    <img src={sl} alt="Substantive Law" title="Substantive Law" />
                                  </ListGroup.Item>
                                  {year_type === "past" ? (
                                    <ListGroup.Item as="li">
                                      <button onClick={() => exportCpdPoints(cpd_year_id)} className="cpd_export_btn">
                                        Export
                                      </button>
                                    </ListGroup.Item>
                                  ) : null}
                                </ListGroup>
                              </div>
                              <div className="cpd_point_data_head">
                                <ListGroup horizontal as="ul" className="align-items-center">
                                  <ListGroup.Item as="li">Event</ListGroup.Item>
                                  <ListGroup.Item as="li">Type</ListGroup.Item>
                                  <ListGroup.Item as="li">Date</ListGroup.Item>
                                  <ListGroup.Item as="li">CPD Points</ListGroup.Item>
                                </ListGroup>
                              </div>

                              {cpd_year_programs.map((year_program_inner) => {
                                let program_name = year_program_inner.program_name;
                                let program_name_slug =
                                  year_program_inner.Slug__c !== null && year_program_inner.Slug__c !== undefined ? year_program_inner.Slug__c : "";
                                let attendee_names = year_program_inner.attendees;
                                let order_name = year_program_inner.Shopify_Order_Number__c;
                                let Ethics =
                                  year_program_inner.Ethics_Professional_Responsibility__c !== null
                                    ? year_program_inner.Ethics_Professional_Responsibility__c
                                    : 0;
                                let Practice_Management =
                                  year_program_inner.Practice_Management_Business_Skills__c !== null
                                    ? year_program_inner.Practice_Management_Business_Skills__c
                                    : 0;
                                let Professional_Skills =
                                  year_program_inner.Professional_Skills__c != null ? year_program_inner.Professional_Skills__c : 0;
                                let Substantive_Law = year_program_inner.Substantive_Law__c !== null ? year_program_inner.Substantive_Law__c : 0;
                                let program_record_type = year_program_inner.product_record_type_name;
                                let program_start_date = year_program_inner.program_start_date_format;
                                let total_event_points =
                                  parseFloat(Ethics) +
                                  parseFloat(Practice_Management) +
                                  parseFloat(Professional_Skills) +
                                  parseFloat(Substantive_Law);

                                return (
                                  <div className="cpd_point_data_listing_block" key={year_program_inner.Id ?? `${order_name}-${program_name}-${program_start_date}`}>
                                    <div className="cpd_point_details_card">
                                      <ListGroup horizontal as="ul" className="align-items-start">
                                        <ListGroup.Item as="li" className="cpd_point_property_block">
                                          <h6 className="cpd_info">
                                            <Link to={"/product/" + program_name_slug}>{program_name}</Link>
                                          </h6>
                                          <div className="cpd_meta_row">
                                            <div className="cpd_meta_col">
                                              <span className="cpd_meta_label">Attendees</span>
                                              <span className="cpd_meta_value">{attendee_names !== undefined && attendee_names !== null ? attendee_names.join(", ") : null}</span>
                                            </div>
                                            <div className="cpd_meta_col">
                                              <span className="cpd_meta_label">Order</span>
                                              <span className="cpd_meta_value">{order_name}</span>
                                            </div>
                                          </div>
                                          <div className="cpd_topics_badges">
                                            {Ethics > 0 && <span className="cpd_badge_item"><span className="cpd_badge_num">{Ethics}</span><img src={ep} alt="Ethics & Professional Responsibility" title="Ethics & Professional Responsibility" /></span>}
                                            {Practice_Management > 0 && <span className="cpd_badge_item"><span className="cpd_badge_num">{Practice_Management}</span><img src={pm} alt="Practice Management & Business Skills" title="Practice Management & Business Skills" /></span>}
                                            {Professional_Skills > 0 && <span className="cpd_badge_item"><span className="cpd_badge_num">{Professional_Skills}</span><img src={ps} alt="Professional Skills" title="Professional Skills" /></span>}
                                            {Substantive_Law > 0 && <span className="cpd_badge_item"><span className="cpd_badge_num">{Substantive_Law}</span><img src={sl} alt="Substantive Law" title="Substantive Law" /></span>}
                                          </div>
                                        </ListGroup.Item>
                                        <ListGroup.Item as="li" className="cpd_point_type">
                                          {program_record_type}
                                        </ListGroup.Item>
                                        <ListGroup.Item as="li" className="cpd_point_date">
                                          {program_start_date}
                                        </ListGroup.Item>
                                        <ListGroup.Item as="li" className="cpd_point_total">
                                          {total_event_points}
                                        </ListGroup.Item>
                                      </ListGroup>
                                    </div>
                                  </div>
                                );
                              })}

                              {CustomcpdPoints[cpd_year_id] !== undefined
                                ? CustomcpdPoints[cpd_year_id].map((custom_program) => {
                                  let total_event_points =
                                    parseInt(custom_program.ep) +
                                    parseInt(custom_program.pm) +
                                    parseInt(custom_program.ps) +
                                    parseInt(custom_program.sl);
                                  let newDate = new Date(custom_program.program_date);
                                  return (
                                    <div className="cpd_point_data_listing_block manually-addded" key={randm()}>
                                      <div className="cpd_point_details_card">
                                        <ListGroup horizontal as="ul" className="align-items-start">
                                          <ListGroup.Item as="li" className="cpd_point_property_block">
                                            <h6 className="cpd_info">{custom_program.program_name}</h6>
                                            <p className="cpd_order_no">Training provider: {custom_program.training_provider}</p>
                                            <div className="cpd_topics_badges">
                                              {custom_program.ep > 0 && <span className="cpd_badge_item"><span className="cpd_badge_num">{custom_program.ep}</span><img src={ep} alt="Ethics & Professional Responsibility" title="Ethics & Professional Responsibility" /></span>}
                                              {custom_program.pm > 0 && <span className="cpd_badge_item"><span className="cpd_badge_num">{custom_program.pm}</span><img src={pm} alt="Practice Management & Business Skills" title="Practice Management & Business Skills" /></span>}
                                              {custom_program.ps > 0 && <span className="cpd_badge_item"><span className="cpd_badge_num">{custom_program.ps}</span><img src={ps} alt="Professional Skills" title="Professional Skills" /></span>}
                                              {custom_program.sl > 0 && <span className="cpd_badge_item"><span className="cpd_badge_num">{custom_program.sl}</span><img src={sl} alt="Substantive Law" title="Substantive Law" /></span>}
                                            </div>
                                          </ListGroup.Item>
                                          <ListGroup.Item as="li" className="cpd_point_type">
                                            {custom_program.program_format}
                                          </ListGroup.Item>
                                          <ListGroup.Item as="li" className="cpd_point_date">
                                            {newDate.getDate() + "/" + twoDigit(newDate.getMonth() + 1) + "/" + newDate.getFullYear()}
                                          </ListGroup.Item>
                                          <ListGroup.Item as="li" className="cpd_point_total">
                                            {total_event_points}
                                          </ListGroup.Item>
                                        </ListGroup>
                                      </div>
                                    </div>
                                  );
                                })
                                : null}
                            </Fragment>
                          );
                        })}
                      </div>
                    ) : loading ? (
                      <div className="pre_loader ae-no-events" style={{ position: 'relative' }}>
                        <LoaderIcon />
                      </div>
                    ) : null}
                    {/* No data + not loading → show empty state */}
                    {!loading &&
                      (cpdPoints === null || cpdPoints === undefined || Object.keys(cpdPoints).length === 0) &&
                      CustomcpdPoints?.[CPDyear()] === undefined && (
                        <div className="ae-no-events">
                          <p>There are no data found with your account.{" "}<a href="mailto:registration@leocussen.edu.au" className="btn-link">Get in touch</a></p>
                        </div>
                      )}
                    {cpdPoints === null || cpdPoints === undefined || Object.keys(cpdPoints).length === 0 ? (
                      CustomcpdPoints?.[CPDyear()] !== undefined ? (
                        <div className="cpd_point_data_wrapper">
                          {CustomcpdPoints[CPDyear()].map((cpd_event_year) => {
                            let cpd_year_id = CPDyear();
                            let Practice_Management_Business_Skills_otal_points = 0;

                            let Professional_Skills_total_points = 0;

                            let Substantive_Law_total_points = 0;

                            let Ethics_total_points = 0;

                            if (CustomcpdPoints[cpd_year_id] !== undefined) {
                              Practice_Management_Business_Skills_otal_points += CustomcpdPoints[cpd_year_id].reduce(function (sum, current) {
                                return parseFloat(sum) + parseFloat(current.pm);
                              }, 0);

                              Professional_Skills_total_points += CustomcpdPoints[cpd_year_id].reduce(function (sum, current) {
                                return parseFloat(sum) + parseFloat(current.ps);
                              }, 0);

                              Substantive_Law_total_points += CustomcpdPoints[cpd_year_id].reduce(function (sum, current) {
                                return parseFloat(sum) + parseFloat(current.sl);
                              }, 0);

                              Ethics_total_points += CustomcpdPoints[cpd_year_id].reduce(function (sum, current) {
                                return parseFloat(sum) + parseFloat(current.ep);
                              }, 0);
                            }

                            let total_points =
                              parseFloat(Practice_Management_Business_Skills_otal_points) +
                              parseFloat(Professional_Skills_total_points) +
                              parseFloat(Substantive_Law_total_points) +
                              parseFloat(Ethics_total_points);

                            return (
                              <Fragment key={cpd_years_index}>
                                <div className="admin_head d-flex flex-wrap align-items-center justify-content-between">
                                  <h5 className="text-secondary display-5 w-100 mb-2">{cpd_year_id}</h5>
                                  <h5 className="m-0 h5 text-dark">My CPD Points: {total_points}</h5>
                                  <ListGroup horizontal as="ul" className="justify-content-between cpd_topics">
                                    <ListGroup.Item as="li">
                                      <span>{Ethics_total_points}</span>
                                      <img src={ep} alt="Ethics & Professional Responsibility" title="Ethics & Professional Responsibility" />
                                    </ListGroup.Item>
                                    <ListGroup.Item as="li">
                                      <span>{Practice_Management_Business_Skills_otal_points}</span>
                                      <img src={pm} alt="Practice Management & Business Skills" title="Practice Management & Business Skills" />
                                    </ListGroup.Item>
                                    <ListGroup.Item as="li">
                                      <span>{Professional_Skills_total_points}</span>
                                      <img src={ps} alt="Professional Skills" title="Professional Skills" />
                                    </ListGroup.Item>
                                    <ListGroup.Item as="li">
                                      <span>{Substantive_Law_total_points}</span>
                                      <img src={sl} alt="Substantive Law" title="Substantive Law" />
                                    </ListGroup.Item>
                                  </ListGroup>
                                </div>
                                <div className="cpd_point_data_head">
                                  <ListGroup horizontal as="ul" className="align-items-center">
                                    <ListGroup.Item as="li">Event</ListGroup.Item>
                                    <ListGroup.Item as="li">Type</ListGroup.Item>
                                    <ListGroup.Item as="li">Date</ListGroup.Item>
                                    <ListGroup.Item as="li">CPD Points</ListGroup.Item>
                                  </ListGroup>
                                </div>

                                {CustomcpdPoints[cpd_year_id] !== undefined
                                  ? CustomcpdPoints[cpd_year_id].map((custom_program) => {
                                    let total_event_points =
                                      parseInt(custom_program.ep) +
                                      parseInt(custom_program.pm) +
                                      parseInt(custom_program.ps) +
                                      parseInt(custom_program.sl);
                                    let newDate = new Date(custom_program.program_date);
                                    return (
                                      <div className="cpd_point_data_listing_block manually-addded" key={randm()}>
                                        <div className="cpd_point_details_card">
                                          <ListGroup horizontal as="ul" className="align-items-start">
                                            <ListGroup.Item as="li" className="cpd_point_property_block">
                                              <h6 className="cpd_info">{custom_program.program_name}</h6>
                                              <p className="cpd_order_no">Training provider: {custom_program.training_provider}</p>
                                              <div className="cpd_topics_badges">
                                                {custom_program.ep > 0 && <span className="cpd_badge_item"><span className="cpd_badge_num">{custom_program.ep}</span><img src={ep} alt="Ethics & Professional Responsibility" title="Ethics & Professional Responsibility" /></span>}
                                                {custom_program.pm > 0 && <span className="cpd_badge_item"><span className="cpd_badge_num">{custom_program.pm}</span><img src={pm} alt="Practice Management & Business Skills" title="Practice Management & Business Skills" /></span>}
                                                {custom_program.ps > 0 && <span className="cpd_badge_item"><span className="cpd_badge_num">{custom_program.ps}</span><img src={ps} alt="Professional Skills" title="Professional Skills" /></span>}
                                                {custom_program.sl > 0 && <span className="cpd_badge_item"><span className="cpd_badge_num">{custom_program.sl}</span><img src={sl} alt="Substantive Law" title="Substantive Law" /></span>}
                                              </div>
                                            </ListGroup.Item>
                                            <ListGroup.Item as="li" className="cpd_point_type">
                                              {custom_program.program_format}
                                            </ListGroup.Item>
                                            <ListGroup.Item as="li" className="cpd_point_date">
                                              {newDate.getDate() + "/" + twoDigit(newDate.getMonth() + 1) + "/" + newDate.getFullYear()}
                                            </ListGroup.Item>
                                            <ListGroup.Item as="li" className="cpd_point_total">
                                              {total_event_points}
                                            </ListGroup.Item>
                                          </ListGroup>
                                        </div>
                                      </div>
                                    );
                                  })
                                  : null}
                              </Fragment>
                            );
                          })}
                        </div>
                      ) : null
                    ) : null}
                  </div>
                </Col>
              </Row>
            </div>
          ) : (
            <div className="text-center">
              <h3>Please Login</h3>
              <button onClick={() => My_Account_button_click()} className="btn btn-primary rounded-pill m-0 stest">
                {" "}Login
              </button>
            </div>
          )}
        </Container>
        <ManuallyCPD EngineURL={props.EngineURL} cdp_point_popup={cdp_point_popup} set_cpd_point_popup={set_cpd_point_popup} email={userEmail} userToken={userToken} RefreshData={getData} />
      </div>
      <div className={`admin_sidebar_mobile${mobileSidebarOpen ? ' sidebar-open' : ''}`}>
        <div className="admin_sidebar_drawer">
          <AdminSidebar doLogout={doLogout} />
        </div>
        <button
          className="admin_sidebar_trigger"
          onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
          aria-expanded={mobileSidebarOpen}
        >
          <span className="admin_sidebar_trigger_label">{activeLabel}</span>
          <svg className="admin_sidebar_trigger_dots" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="12" cy="5" r="1.5" fill="#1C1C1C" />
            <circle cx="12" cy="12" r="1.5" fill="#1C1C1C" />
            <circle cx="12" cy="19" r="1.5" fill="#1C1C1C" />
          </svg>
        </button>
      </div>
    </>
  );
};
